import React from 'react';
import Button from '../../UI/Button';
import Input from '../../UI/Input';
import Select from '../../UI/Select';
import { MilestoneStatus } from './MilestoneStatusBadge';

export interface MilestoneFormData {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  scheduledDate: string | Date;
  completedDate?: string | Date;
  status: MilestoneStatus;
  delayReason?: string;
  earlyCompletion?: boolean;
  earlyCompletionReason?: string;
}

interface MilestoneFormProps {
  formData: MilestoneFormData;
  onFormDataChange: (data: Partial<MilestoneFormData>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
}

const MilestoneForm: React.FC<MilestoneFormProps> = ({
  formData,
  onFormDataChange,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Save',
}) => {
  const handleChange = (field: keyof MilestoneFormData, value: any) => {
    onFormDataChange({ [field]: value });
  };

  const getDateValue = (date: string | Date | undefined): string => {
    if (!date) return '';
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    return '';
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h4 className="font-medium mb-3">Milestone Details</h4>
      <div className="space-y-3">
        <Input
          label="Title"
          value={formData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          required
          placeholder="Milestone title"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Milestone description"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
            <input
              type="date"
              value={getDateValue(formData.scheduledDate)}
              onChange={(e) => handleChange('scheduledDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <Select
            label="Status"
            value={formData.status || 'pending'}
            onChange={(e) => handleChange('status', e.target.value as MilestoneStatus)}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'delayed', label: 'Delayed' },
            ]}
          />
        </div>
        {formData.status === 'delayed' && (
          <Input
            label="Delay Reason"
            value={formData.delayReason || ''}
            onChange={(e) => handleChange('delayReason', e.target.value)}
            placeholder="Reason for delay"
          />
        )}
        {formData.status === 'completed' && (
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.earlyCompletion || false}
                onChange={(e) => handleChange('earlyCompletion', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Completed Early</span>
            </label>
            {formData.earlyCompletion && (
              <Input
                label="Early Completion Reason"
                value={formData.earlyCompletionReason || ''}
                onChange={(e) => handleChange('earlyCompletionReason', e.target.value)}
                placeholder="Reason for early completion"
              />
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={onSubmit}
            disabled={!formData.title || !formData.scheduledDate || isLoading}
            isLoading={isLoading}
          >
            {submitLabel}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneForm;

